import { annotate } from 'rough-notation';

// Set to true while debugging; the highlighter traces every step.
const DEBUG = false;
const debug = (...args) => { if (DEBUG) console.log(...args); };

export class GrammarHighlighter {
  constructor(container, options = {}) {
    this.container = container;
    this.matchMode = options.matchMode || 'token';
    this.iouThreshold = options.iouThreshold || 0.9;
    
    // Single source of truth for all grammar categories
    this.GRAMMAR_CATEGORIES = {
      "S": { "de": "Subjekt" },
      "P": { "de": "Prädikat (finites Verb)" },
      "AO": { "de": "Akkusativobjekt / direktes Objekt" },
      "DO": { "de": "Dativobjekt / indirektes Objekt" },
      "PO": { "de": "Präpositionalobjekt" },
      "PN": { "de": "Prädikatsnomen" },
      "PA": { "de": "Prädikatsadjektiv" },
      "OC": { "de": "Objektprädikativ" },
      "AdvT": { "de": "Adverbiale Bestimmung der Zeit" },
      "AdvL": { "de": "Adverbiale Bestimmung des Ortes" },
      "AdvM": { "de": "Adverbiale Bestimmung der Art und Weise" },
      "AdvC": { "de": "Adverbiale Bestimmung des Grundes / Bedingung / Einräumung" },
      "AcI": { "de": "Accusativus cum Infinitivo" },
      "AblAbs": { "de": "Ablativus Absolutus" },
      "RelCl": { "de": "Relativsatz" },
      "CumCl": { "de": "cum-Satz" },
      "UtCl": { "de": "ut-Satz (Finalsatz / Konsekutivsatz)" },
      "IndQ": { "de": "indirekte Frage" },
      "IndSt": { "de": "indirekte Rede / Aussage" },
      "InfCl": { "de": "Infinitivkonstruktion" },
      "Atr": { "de": "Attribut" },
      "App": { "de": "Apposition" },
      "Voc": { "de": "Vokativ" },
      "SubCl": { "de": "Nebensatz (allgemein)" }
    };
    
    this.categories = [];
    this.groundTruth = [];
    this.userSelections = [];
    this.currentText = '';
    this.selectionId = 0;

    this.pendingSelection = null;
    this.percentageDisplay = null;
    this.progressDetails = null;
    
     // Pre-selection system
     this.preSelectedWord = null;
     this.preSelectionElement = null;
     
    // Keyboard shortcut system
    this.keyBuffer = '';
    this.keyTimeout = null;
    this.keyTimeoutDuration = 1000; // 1 second timeout
    
    // Generate shortcuts for found categories
    this.globalShortcuts = {};
    
    // Highlight locking state
    this.highlightsLocked = false;
    this.init();
  }

  // Extract color from CSS for rough notation
  getCategoryColor(category) {
    // Create a temporary element with the appropriate CSS class to extract the color
    const tempElement = document.createElement('span');
    let cssClass;
    if (category === 'AdvT') {
      cssClass = 'at';
    } else if (category === 'AO') {
      cssClass = 'ao'; // This will use 'ao' CSS but we'll map it to 'do' if needed
    } else {
      cssClass = category.toLowerCase();
    }
    
    tempElement.className = `gh-highlight ${cssClass}`;
    document.body.appendChild(tempElement);
    
    const computedStyle = window.getComputedStyle(tempElement);
    const borderColor = computedStyle.borderColor;
    
    document.body.removeChild(tempElement);
    
    // Convert the color to a lighter version for rough notation
    return this.lightenColor(borderColor) || 'var(--bs-secondary-color)';
  }
  
  // Get the category color for buttons (darker version)
  getCategoryButtonColor(category) {
    // Create a temporary element with the appropriate CSS class to extract the color
    const tempElement = document.createElement('span');
    let cssClass;
    if (category === 'AdvT') {
      cssClass = 'at';
    } else if (category === 'AO') {
      cssClass = 'ao';
    } else {
      cssClass = category.toLowerCase();
    }
    
    tempElement.className = `gh-highlight ${cssClass}`;
    document.body.appendChild(tempElement);
    
    const computedStyle = window.getComputedStyle(tempElement);
    const borderColor = computedStyle.borderColor;
    
    document.body.removeChild(tempElement);
    
    // Return the original color (not lightened) for buttons
    return borderColor || 'var(--bs-secondary)';
  }
  
// Helper function to darken a color for button hover effects
darkenColor(color) {
  if (!color) return 'var(--bs-secondary)';
  
  // Handle rgb(r, g, b) format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    const r = Math.max(0, parseInt(rgbMatch[1]) - 30);
    const g = Math.max(0, parseInt(rgbMatch[2]) - 30);
    const b = Math.max(0, parseInt(rgbMatch[3]) - 30);
    return `rgb(${r}, ${g}, ${b})`;
  }
  
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = Math.max(0, parseInt(hex.slice(0, 2), 16) - 30);
    const g = Math.max(0, parseInt(hex.slice(2, 4), 16) - 30);
    const b = Math.max(0, parseInt(hex.slice(4, 6), 16) - 30);
    const newHex = [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    return `#${newHex}`;
  }
  
  return color;
}

  // Apply rough notation to a specific selection or all selections needing it
  applyRoughNotationToSelection(selectionId) {
    // Find the selection - it might have been merged, so find by ID or apply to all unnotated
    const selection = this.userSelections.find(s => s.id == selectionId);
    
    if (selection && selection.element && !selection.element.roughAnnotation) {
      this.applyRoughNotationToElement(selection.element, selection.category);
    }
    
    // Also apply to any other selections that don't have rough notation yet
    this.userSelections.forEach(sel => {
      if (sel.element && !sel.element.roughAnnotation) {
        this.applyRoughNotationToElement(sel.element, sel.category);
      }
    });
  }
  
  // Apply rough notation to a single element
  applyRoughNotationToElement(element, category) {
    // Don't apply if already has annotation
    if (element.roughAnnotation) {
      return;
    }
    
    try {
    const annotation = annotate(element, {
      type: 'highlight',
      color: this.getCategoryColor(category),
      strokeWidth: 2,
      animate: false,
      padding: 2
    });
    annotation.show();
    element.roughAnnotation = annotation;
      debug('Applied rough notation to element:', element.textContent);
    } catch (error) {
      console.error('Failed to apply rough notation:', error);
    }
  }
  
  // Helper function to lighten a color
  lightenColor(color) {
    if (!color) return 'var(--bs-secondary-color)';
    
    // Handle rgb(r, g, b) format
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Lighten by mixing with white (add 40% white)
      const lightenFactor = 0.6;
      const newR = Math.round(r + (255 - r) * lightenFactor);
      const newG = Math.round(g + (255 - g) * lightenFactor);
      const newB = Math.round(b + (255 - b) * lightenFactor);
      
      return `rgb(${newR}, ${newG}, ${newB})`;
    }
    
    // Handle hex colors
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      
      const lightenFactor = 0.6;
      const newR = Math.round(r + (255 - r) * lightenFactor);
      const newG = Math.round(g + (255 - g) * lightenFactor);
      const newB = Math.round(b + (255 - b) * lightenFactor);
      
      return `rgb(${newR}, ${newG}, ${newB})`;
    }
    
    return color;
  }

  // Computed properties derived from single source of truth
  get ALL_GRAMMAR_CATEGORIES() {
    return Object.keys(this.GRAMMAR_CATEGORIES);
  }

  get categoryDict() {
    return this.GRAMMAR_CATEGORIES;
  }

  get allCategories() {
    return this.ALL_GRAMMAR_CATEGORIES;
  }
 generateGlobalShortcuts(categories) {
   return this.generateShortcuts(categories);
 }
 
 generateShortcuts(categories) {
   const shortcuts = {};
   
  // Special case: Use 'at' for AdvT to avoid adblocker conflicts with 'advt'
  if (categories.includes('AdvT')) {
    shortcuts['AdvT'] = 'at';
  }
  
   const categoryData = categories.map(category => ({
     category,
     sources: [
       category.toLowerCase(),
       this.categoryDict[category]?.de.toLowerCase().replace(/[^a-z]/g, '') || category.toLowerCase()
     ]
   }));
   
   for (let length = 1; length <= 6; length++) {
     const candidateShortcuts = new Map();
     const conflicts = new Set();
     
     categoryData.forEach(data => {
       if (shortcuts[data.category]) return; // Already assigned
       
       let bestCandidate = null;
       for (const source of data.sources) {
         if (source.length >= length) {
           bestCandidate = source.substring(0, length);
           break;
         }
       }
       if (bestCandidate) {
         candidateShortcuts.set(data.category, bestCandidate);
       }
     });
     
     // Find direct conflicts
     const shortcutToCategories = new Map();
     candidateShortcuts.forEach((shortcut, category) => {
       if (!shortcutToCategories.has(shortcut)) {
         shortcutToCategories.set(shortcut, []);
       }
       shortcutToCategories.get(shortcut).push(category);
     });
     
     shortcutToCategories.forEach((cats, shortcut) => {
       if (cats.length > 1) {
         cats.forEach(cat => conflicts.add(cat));
       }
     });
     
     // Find prefix conflicts
     candidateShortcuts.forEach((shortcut1, cat1) => {
       candidateShortcuts.forEach((shortcut2, cat2) => {
         if (cat1 !== cat2) {
           if (shortcut1.startsWith(shortcut2) || shortcut2.startsWith(shortcut1)) {
             conflicts.add(cat1);
             conflicts.add(cat2);
           }
         }
       });
     });
     
     // Assign non-conflicting shortcuts
     candidateShortcuts.forEach((shortcut, category) => {
       if (!conflicts.has(category) && !shortcuts[category]) {
         shortcuts[category] = shortcut;
       }
     });
     
     if (Object.keys(shortcuts).length === categories.length) {
       break;
   }
   }
   
   return shortcuts;
 }
  init() {
    // Add CSS styles for highlighting (once per page, not per instance)
   if (!document.getElementById('ph-grammar-highlighter-style')) {
   const style = document.createElement('style');
   style.id = 'ph-grammar-highlighter-style';
   style.textContent = `
     .unmarked-text {
       user-select: none;
       -webkit-user-select: none;
       -moz-user-select: none;
       -ms-user-select: none;
      display: inline;
      white-space: pre-wrap;
       /* Prevent selection extension across boundaries */
       -webkit-touch-callout: none;
       -webkit-tap-highlight-color: transparent;
       /* Create stronger selection barriers */
       -webkit-user-drag: none;
       -moz-user-drag: none;
       user-drag: none;
    }
    .marked-text {
      display: inline;
      white-space: pre-wrap;
       /* Ensure marked text can be selected */
       user-select: text;
       -webkit-user-select: text;
       -moz-user-select: text;
       -ms-user-select: text;
       /* Create clear selection boundaries */
       isolation: isolate;
     }
      .gh-highlight {
        display: inline;
        white-space: pre-wrap;
       /* Ensure highlights can be selected */
       user-select: text;
       -webkit-user-select: text;
       -moz-user-select: text;
       -ms-user-select: text;
      }
      .word-preview {
        border: 2px dotted var(--bs-border-color);
        border-radius: 3px;
        padding: 1px;
        margin: -1px;
        background: var(--bs-tertiary-bg);
        cursor: pointer;
        display: inline;
      }
      .word-preview:hover {
        border-color: var(--bs-emphasis-color);
        background: var(--bs-secondary-bg-subtle);
      }
   `;
   document.head.appendChild(style);
   }
    
    // Set up event listeners
    // Replace selection handling with click-based word selection
    this.container.addEventListener('click', (e) => this.handleWordClick(e));
    
    // Set up keyboard shortcuts
    document.addEventListener('keydown', (e) => this.handleKeyboardShortcut(e));
  
    // Make container selectable
    this.container.style.userSelect = 'text';
    this.container.style.cursor = 'text';
  }

  handleWordClick(event) {
    // Prevent default selection behavior
    event.preventDefault();
    
    // Clear any existing word selection
    window.getSelection().removeAllRanges();
    
    // Find the clicked text node and word
    const clickedElement = event.target;
    const clickResult = this.findClickedWord(clickedElement, event);
    
    if (!clickResult) {
      this.clearWordPreview();
      return;
    }
    
    const { word, textNode, startOffset, endOffset } = clickResult;
    
    // Check if the word is in a marked text section
    if (!this.isInMarkedText(textNode)) {
      debug('Clicked word is not in marked text:', word);
      this.clearWordPreview();
      return;
    }
    
    debug('Selected word for preview:', word);
    
    // Create word preview
    this.createWordPreview(textNode, startOffset, endOffset, word);
  }
  
  findClickedWord(element, event) {
    // Use mouse coordinates to find the exact position that was clicked
    const x = event.clientX;
    const y = event.clientY;
    
    debug('Click coordinates:', x, y);
    
    // Find the text position at the click coordinates
    let range = null;
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(x, y);
    } else if (document.caretPositionFromPoint) {
      const caretPos = document.caretPositionFromPoint(x, y);
      if (caretPos) {
        range = document.createRange();
        range.setStart(caretPos.offsetNode, caretPos.offset);
        range.setEnd(caretPos.offsetNode, caretPos.offset);
      }
    }
    
    if (!range) {
      debug('Could not determine click position in text');
      return null;
    }
    
    const clickedNode = range.startContainer;
    const clickOffset = range.startOffset;
    
    debug('Clicked in node:', clickedNode.textContent);
    debug('At offset:', clickOffset);
    
    // Make sure we clicked in a text node
    if (clickedNode.nodeType !== Node.TEXT_NODE) {
      debug('Click was not in a text node');
      return null;
    }
    
    // Find word boundaries around the click position
    const text = clickedNode.textContent;
    
    // Find the start of the word containing the click position
    let wordStart = clickOffset;
    while (wordStart > 0 && !/\s/.test(text[wordStart - 1])) {
      wordStart--;
    }
    
    // Find the end of the word containing the click position
    let wordEnd = clickOffset;
    while (wordEnd < text.length && !/\s/.test(text[wordEnd])) {
      wordEnd++;
    }
    
    // Extract the word
    const word = text.substring(wordStart, wordEnd).trim();
    
    if (!word) {
      debug('No word found at click position');
      return null;
    }
    
    debug('Found clicked word:', `"${word}" at ${wordStart}-${wordEnd}`);
    
    return {
      word: word,
      textNode: clickedNode,
      startOffset: wordStart,
      endOffset: wordEnd
    };
  }
  
  isInMarkedText(node) {
    let parent = node.parentNode;
    while (parent && parent !== this.container) {
      if (parent.nodeType === Node.ELEMENT_NODE && 
          parent.classList && 
          parent.classList.contains('marked-text')) {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  }
  
  createWordPreview(textNode, startOffset, endOffset, word) {
    // Clear any existing preview
    this.clearWordPreview();
    
    try {
      // Create range for the word
      const range = document.createRange();
      range.setStart(textNode, startOffset);
      range.setEnd(textNode, endOffset);
      
      // Create preview element
      const preview = document.createElement('span');
      preview.className = 'word-preview';
      preview.textContent = word;
      
      // Replace the text with the preview
      const beforeText = textNode.textContent.substring(0, startOffset);
      const afterText = textNode.textContent.substring(endOffset);
      
      const parent = textNode.parentNode;
      
      // Create new text nodes
      const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
      const afterNode = afterText ? document.createTextNode(afterText) : null;
      
      // Replace the original text node with the new structure
      if (beforeNode) parent.insertBefore(beforeNode, textNode);
      parent.insertBefore(preview, textNode);
      if (afterNode) parent.insertBefore(afterNode, textNode);
      parent.removeChild(textNode);
      
      // Store the preview information
      this.preSelectedWord = {
        word: word,
        element: preview,
        originalParent: parent,
        beforeNode: beforeNode,
        afterNode: afterNode
      };
      
      this.preSelectionElement = preview;
      
      debug('Created word preview for:', word);
      
    } catch (error) {
      console.error('Error creating word preview:', error);
      this.clearWordPreview();
    }
  }
  
  clearWordPreview() {
    if (this.preSelectionElement && this.preSelectedWord) {
      try {
        const { element, originalParent, beforeNode, afterNode, word } = this.preSelectedWord;
        
        // Reconstruct the original text node
        const beforeText = beforeNode ? beforeNode.textContent : '';
        const afterText = afterNode ? afterNode.textContent : '';
        const fullText = beforeText + word + afterText;
        
        const newTextNode = document.createTextNode(fullText);
        
        // Replace the preview structure with the original text node
        if (beforeNode) originalParent.removeChild(beforeNode);
        originalParent.replaceChild(newTextNode, element);
        if (afterNode) originalParent.removeChild(afterNode);
        
        debug('Cleared word preview');
        
      } catch (error) {
        console.error('Error clearing word preview:', error);
      }
    }
    
    this.preSelectedWord = null;
    this.preSelectionElement = null;
  }

  applyHighlightToPreSelected(category) {
    if (!this.preSelectedWord) {
      debug('No pre-selected word to highlight');
      return;
    }
    
    const { word, element } = this.preSelectedWord;
    debug('Applying highlight to pre-selected word:', word, 'category:', category);
    
    try {
      // Create highlight element
      const highlight = document.createElement('span');
      const cssClass = category === 'AdvT' ? 'at' : category.toLowerCase();
      highlight.className = `gh-highlight ${cssClass}`;
      highlight.dataset.category = this.categoryDict[category]?.de || category;
      highlight.dataset.id = ++this.selectionId;
      highlight.textContent = word;
      highlight.style.whiteSpace = 'pre-wrap';
      
      // Add click listener to remove highlight
      highlight.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!this.highlightsLocked) {
          this.removeSelection(highlight.dataset.id);
        }
      });
      
      // Replace the preview element with the highlight
      const parent = element.parentNode;
      parent.replaceChild(highlight, element);
      
      // Store user selection
      this.userSelections.push({
        id: this.selectionId,
        text: word,
        category: category,
        tokens: this.tokenize(word),
        element: highlight
      });
      
      // Apply rough notation
      this.applyRoughNotationToElement(highlight, category);
      
      debug('Stored user selection:', {
        id: this.selectionId,
        text: word,
        category: category,
        element: highlight
      });
      
      // Clear the pre-selection state
      this.preSelectedWord = null;
      this.preSelectionElement = null;
      
      // Try to merge with adjacent highlights of the same category
      this.tryMergeAdjacentHighlights(this.selectionId);
      
      // Update progress display
      this.updateProgressDisplay();
      
      debug('Successfully applied highlight to:', word);
      
    } catch (error) {
      console.error('Error applying highlight to pre-selected word:', error);
      this.clearWordPreview();
    }
  }

  createProgressIndicator() {
    // Create percentage display
    this.percentageDisplay = document.createElement('span');
    this.percentageDisplay.style.cssText = 'margin-left: 15px; font-weight: bold; color: var(--bs-secondary-color); font-size: 14px;';
    this.percentageDisplay.textContent = '0%';
    
    // Create details display
    this.progressDetails = document.createElement('span');
    this.progressDetails.style.cssText = 'margin-left: 5px; color: var(--bs-tertiary-color); font-size: 12px;';
    this.progressDetails.textContent = '(0/0)';
    
    // Append directly to the end of the sentence
    this.container.appendChild(this.percentageDisplay);
    this.container.appendChild(this.progressDetails);
  }

  loadFromMarkedHTML(html) {
    debug('loadFromMarkedHTML called with:', html.substring(0, 200) + '...');
    
    // Parse ground truth from marked HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
   // First, extract the plain text to determine sentences
   this.currentText = this.extractCleanText(tempDiv);
   debug('Extracted text:', this.currentText.substring(0, 100) + '...');
   
   // Split into sentences to create sentence mapping
   const sentenceRegex = /[.!?](?:\s+|$)/g;
   const sentences = [];
   let lastIndex = 0;
   let match;
   
   while ((match = sentenceRegex.exec(this.currentText)) !== null) {
     const sentence = this.currentText.substring(lastIndex, match.index + 1).trim();
     if (sentence) {
       sentences.push(sentence);
     }
     lastIndex = sentenceRegex.lastIndex;
   }
   
   // Handle any remaining text
   if (lastIndex < this.currentText.length) {
     const remaining = this.currentText.substring(lastIndex).trim();
     if (remaining) {
       sentences.push(remaining);
     }
   }
   
   debug('Found sentences:', sentences.length);
   
    this.groundTruth = [];
    const groundTruthMap = new Map(); // Track duplicates
    this.markedSegments = []; // Track which text segments are marked for highlighting
    const foundCategories = new Set();
    const marks = tempDiv.querySelectorAll('mark[data-cat]');
    debug('Found', marks.length, 'marked elements');
    
   // Get the full plain text to calculate positions
   const fullText = this.currentText;
   let textPosition = 0;
   
    marks.forEach(mark => {
      const category = mark.dataset.cat;
      foundCategories.add(category);
      const text = mark.textContent.trim().replace(/\s+/g, ' ');
      
      // Find the position of this mark in the full text
      const markPosition = fullText.indexOf(text, textPosition);
      if (markPosition !== -1) {
        textPosition = markPosition + text.length;
        
        // Track this marked segment
        this.markedSegments.push({
          start: markPosition,
          end: markPosition + text.length,
          text: text,
          category: category
        });
      }
      
      // Find which sentence contains this position
      let sentenceIndex = -1;
      let currentPos = 0;
      for (let i = 0; i < sentences.length; i++) {
        const sentenceEnd = currentPos + sentences[i].length;
        if (markPosition >= currentPos && markPosition < sentenceEnd) {
          sentenceIndex = i;
          break;
        }
        currentPos = sentenceEnd + 1; // +1 for space between sentences
      }
      
     // Track duplicates
     const key = `${sentenceIndex}:${text}:${category}`;
     if (groundTruthMap.has(key)) {
       console.warn(`Duplicate ground truth found: "${text}" (${category}) in sentence ${sentenceIndex}`);
     }
     groundTruthMap.set(key, true);
     
      this.groundTruth.push({
        text: text,
        category: category,
        tokens: this.tokenize(text),
        sentenceIndex: sentenceIndex
      });
    });
     
     // Sort marked segments by position for easier processing
     this.markedSegments.sort((a, b) => a.start - b.start);
     debug('Marked segments:', this.markedSegments);
    
    // Update categories and create buttons
    this.categories = Array.from(foundCategories);
    debug('Found categories:', this.categories);
    
   // Generate dynamic shortcuts for found categories
   this.globalShortcuts = this.generateGlobalShortcuts(this.categories);
   debug('Generated shortcuts:', this.globalShortcuts);
   
    // Set the plain text content (back to original)
    // Handle nested HTML structures (like p tags) by getting clean text content
    this.currentText = this.extractCleanText(tempDiv);
    debug('Extracted text:', this.currentText.substring(0, 100) + '...');
    
    // Split into sentences and create sentence containers (back to original)
    this.createSentenceContainers();
    
    // Create buttons above the syntax div
    this.createCategoryButtons();
    
    // Clear any existing selections
    this.userSelections = [];

 // Update progress display to show ground truth counts
 this.updateProgressDisplay();
 
    debug('Grammar highlighter initialization complete');
  }

  extractCleanText(element) {
    // Get text content while preserving sentence structure
    let text = '';
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let node;
    while (node = walker.nextNode()) {
      text += node.textContent;
    }
    
    // Clean up extra whitespace while preserving sentence boundaries
    return text.replace(/\s+/g, ' ').trim();
  }
  createSentenceContainers() {
    // Split text into sentences (simple regex for Latin text)
    // Matches: period, exclamation, question mark followed by space or end of string
    const sentenceRegex = /[.!?](?:\s+|$)/g;
    const sentences = [];
    let lastIndex = 0;
    let match;
    
    while ((match = sentenceRegex.exec(this.currentText)) !== null) {
      const sentence = this.currentText.substring(lastIndex, match.index + 1).trim();
      if (sentence) {
        sentences.push(sentence);
      }
      lastIndex = sentenceRegex.lastIndex;
    }
    
    // Handle any remaining text
    if (lastIndex < this.currentText.length) {
      const remaining = this.currentText.substring(lastIndex).trim();
      if (remaining) {
        sentences.push(remaining);
      }
    }
    
    // Clear the container
    this.container.innerHTML = '';
    
    // Create a div for each sentence
    this.sentenceContainers = [];
    sentences.forEach((sentence, index) => {
      const sentenceDiv = document.createElement('div');
      sentenceDiv.className = 'sentence';
      sentenceDiv.style.cssText = 'margin: 10px 0; padding: 8px; position: relative;';
      
      // Create a span specifically for the sentence text with marked/unmarked segments
      const textSpan = document.createElement('span');
      textSpan.className = 'sentence-text';
      
      // Find the start position of this sentence in the full text
      let sentenceStartPos = 0;
      for (let i = 0; i < index; i++) {
        sentenceStartPos += sentences[i].length + 1; // +1 for space between sentences
      }
      
      // Get marked segments that fall within this sentence
      const sentenceMarkedSegments = this.markedSegments.filter(segment => {
        const segmentStart = segment.start;
        const segmentEnd = segment.end;
        const sentenceEnd = sentenceStartPos + sentence.length;
        return segmentStart >= sentenceStartPos && segmentEnd <= sentenceEnd;
      });
      
      // Build the sentence with marked and unmarked segments
      if (sentenceMarkedSegments.length === 0) {
        // No marked segments in this sentence - make entire sentence non-selectable
        const unmarkedSpan = document.createElement('span');
        unmarkedSpan.className = 'unmarked-text';
        unmarkedSpan.textContent = sentence;
        textSpan.appendChild(unmarkedSpan);
      } else {
        // Mix of marked and unmarked segments
        let currentPos = 0; // Position within the sentence
        
        // Group consecutive marked segments together
        let segIndex = 0;
        while (segIndex < sentenceMarkedSegments.length) {
          const segment = sentenceMarkedSegments[segIndex];
          const relativeStart = segment.start - sentenceStartPos;
          const relativeEnd = segment.end - sentenceStartPos;
          
          // Add text before this marked segment
          if (currentPos < relativeStart) {
            const unmarkedText = sentence.substring(currentPos, relativeStart);
            if (unmarkedText) {
              // Check if this is just whitespace between marked segments
              if (/^\s+$/.test(unmarkedText)) {
                // Just whitespace - preserve as span to ensure consistent rendering across browsers
                const spaceSpan = document.createElement('span');
                spaceSpan.style.whiteSpace = 'pre-wrap';
                spaceSpan.textContent = unmarkedText;
                textSpan.appendChild(spaceSpan);
              } else {
                // Contains non-whitespace - wrap in unmarked-text span
                 // Add selection barrier before unmarked text if preceded by marked text
                 if (currentPos > 0) {
                   const barrier = document.createElement('span');
                   barrier.className = 'selection-barrier';
                   barrier.style.cssText = 'display: inline-block; width: 0; height: 0; user-select: none; font-size: 0; line-height: 0;';
                   barrier.textContent = '\u200C'; // Zero-width non-joiner
                   textSpan.appendChild(barrier);
                 }
                 
                const unmarkedSpan = document.createElement('span');
                unmarkedSpan.className = 'unmarked-text';
                 unmarkedSpan.style.whiteSpace = 'pre-wrap';
                unmarkedSpan.textContent = unmarkedText;
                textSpan.appendChild(unmarkedSpan);
                 
                 // Add selection barrier after unmarked text
                 const barrier2 = document.createElement('span');
                 barrier2.className = 'selection-barrier';
                 barrier2.style.cssText = 'display: inline-block; width: 0; height: 0; user-select: none; font-size: 0; line-height: 0;';
                 barrier2.textContent = '\u200C'; // Zero-width non-joiner
                 textSpan.appendChild(barrier2);
              }
            }
          }
          
          // Find consecutive marked segments (with only whitespace between them)
          let groupStart = relativeStart;
          let groupEnd = relativeEnd;
          let nextSegIndex = segIndex + 1;
          
          while (nextSegIndex < sentenceMarkedSegments.length) {
            const nextSegment = sentenceMarkedSegments[nextSegIndex];
            const nextRelativeStart = nextSegment.start - sentenceStartPos;
            const nextRelativeEnd = nextSegment.end - sentenceStartPos;
            
            // Check if there's only whitespace between current group end and next segment
            const textBetween = sentence.substring(groupEnd, nextRelativeStart);
            if (/^\s*$/.test(textBetween)) {
              // Only whitespace - include this segment in the group
              groupEnd = nextRelativeEnd;
              nextSegIndex++;
            } else {
              // Non-whitespace content - break the group
              break;
            }
          }
          
          // Create one marked-text span for the entire group
          const groupText = sentence.substring(groupStart, groupEnd);
          const markedSpan = document.createElement('span');
          markedSpan.className = 'marked-text';
          markedSpan.textContent = groupText;
          textSpan.appendChild(markedSpan);
          
          currentPos = groupEnd;
          segIndex = nextSegIndex; // Move to the next unprocessed segment
        }
        
        // Add any remaining text after all marked segments
        if (currentPos < sentence.length) {
          const unmarkedText = sentence.substring(currentPos);
          if (unmarkedText) {
            // Check if this is just whitespace
            if (/^\s+$/.test(unmarkedText)) {
              // Just whitespace - preserve as span to ensure consistent rendering
              const spaceSpan = document.createElement('span');
              spaceSpan.style.whiteSpace = 'pre-wrap';
              spaceSpan.textContent = unmarkedText;
              textSpan.appendChild(spaceSpan);
            } else {
              // Contains non-whitespace - wrap in unmarked-text span
             // Add selection barrier before unmarked text
             const barrier = document.createElement('span');
             barrier.className = 'selection-barrier';
             barrier.style.cssText = 'display: inline-block; width: 0; height: 0; user-select: none; font-size: 0; line-height: 0;';
             barrier.textContent = '\u200C'; // Zero-width non-joiner
             textSpan.appendChild(barrier);
             
              const unmarkedSpan = document.createElement('span');
              unmarkedSpan.className = 'unmarked-text';
               unmarkedSpan.style.whiteSpace = 'pre-wrap';
              unmarkedSpan.textContent = unmarkedText;
              textSpan.appendChild(unmarkedSpan);
             
             // Add selection barrier after unmarked text (end of sentence)
             const barrier2 = document.createElement('span');
             barrier2.className = 'selection-barrier';
             barrier2.style.cssText = 'display: inline-block; width: 0; height: 0; user-select: none; font-size: 0; line-height: 0;';
             barrier2.textContent = '\u200C'; // Zero-width non-joiner
             textSpan.appendChild(barrier2);
            }
          }
        }
      }
      
     debug(`Creating sentence ${index}: "${sentence}"`);
     debug('Created textSpan:', textSpan);
      sentenceDiv.appendChild(textSpan);
     debug('SentenceDiv after adding textSpan:', sentenceDiv.innerHTML);
      
     // Create a container for progress indicators to keep them isolated
     const progressContainer = document.createElement('span');
     progressContainer.className = 'progress-container';
     progressContainer.style.cssText = 'display: inline-block; margin-left: 15px; user-select: none;';
     
      // Create progress indicator for this sentence
      const progressSpan = document.createElement('span');
      progressSpan.className = 'progress';
      progressSpan.style.cssText = 'font-weight: bold; color: var(--bs-secondary-color); font-size: 14px;';
      progressSpan.textContent = '0%';
      
      const detailsSpan = document.createElement('span');
      detailsSpan.className = 'progress-details';
      detailsSpan.style.cssText = 'margin-left: 5px; color: var(--bs-tertiary-color); font-size: 12px;';
      detailsSpan.textContent = '(0/0)';
      
      progressContainer.appendChild(progressSpan);
      progressContainer.appendChild(detailsSpan);
      sentenceDiv.appendChild(progressContainer);
      
      this.container.appendChild(sentenceDiv);
      
      // Store reference to sentence container and its progress elements
      this.sentenceContainers.push({
        element: sentenceDiv,
        text: sentence,
        progressDisplay: progressSpan,
        progressDetails: detailsSpan,
        index: index
      });
    });
  }

  handleSelection() {
    const selection = window.getSelection();
    if (!selection.rangeCount || selection.isCollapsed) {
      return;
    }
    
    const range = selection.getRangeAt(0);
    
     // Clean up selection that may have crossed unmarked text boundaries
     const cleanedRange = this.cleanSelectionRange(range);
     if (!cleanedRange) {
       debug('Selection crosses unmarked text boundaries, ignoring');
       selection.removeAllRanges();
       return;
     }
     
    // Check if selection is within marked text only
    if (!this.isSelectionInMarkedText(cleanedRange)) {
      debug('Selection contains unmarked text, ignoring');
      selection.removeAllRanges();
      return;
    }
    
    // Find which sentence container contains this selection
    let sentenceElement = null;
    for (const sentenceContainer of this.sentenceContainers) {
      if (sentenceContainer.element.contains(cleanedRange.commonAncestorContainer)) {
        sentenceElement = sentenceContainer.element;
        break;
      }
    }
    
    if (!sentenceElement) {
      return; // Selection not in any sentence
    }
    
    // Store selection info for later use
    this.pendingSelection = {
      text: this.cleanSelectionText(selection.toString()),
      range: this.trimSelectionRange(cleanedRange),
      sentenceElement: sentenceElement
    };
    
    // Clear selection after 10 seconds
    clearTimeout(this.selectionTimeout);
    this.selectionTimeout = setTimeout(() => {
      this.pendingSelection = null;
    }, 10000);
  }
  
  // Check if a selection range is entirely within marked text
  isSelectionInMarkedText(range) {
    // Get all nodes in the range
    const walker = document.createTreeWalker(
      range.commonAncestorContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function(node) {
          // Only include text nodes that intersect with the range
          const nodeRange = document.createRange();
          nodeRange.selectNode(node);
          return range.intersectsNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      }
    );
    
    let node;
    while (node = walker.nextNode()) {
      // Check if this text node is within the selection range
      const nodeRange = document.createRange();
      nodeRange.selectNode(node);
      
      if (range.intersectsNode(node)) {
        // Check if this text node is within unmarked text
        let parent = node.parentNode;
        
        while (parent) {
          if (parent.classList && parent.classList.contains('unmarked-text')) {
            debug('Selection contains unmarked text node:', node.textContent);
            return false;
          }
         // Allow selections within marked-text spans
         if (parent.classList && parent.classList.contains('marked-text')) {
           break; // This is valid marked text, continue to next node
         }
          // Stop searching when we reach the sentence container
          if (parent.classList && parent.classList.contains('sentence')) {
            break;
          }
          parent = parent.parentNode;
        }
      }
    }
    
    // If we get here, all text nodes in the selection are either marked text
    // (not within unmarked-text spans) or are acceptable
    return true;
  }

  createCategoryButtons() {
    // Create a button container above this syntax div
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'category-buttons';
    buttonContainer.style.cssText = 'margin-bottom: 10px; padding: 10px; background: var(--bs-tertiary-bg); border-radius: 5px;';
    
    // Insert the button container before the syntax div
    this.container.parentNode.insertBefore(buttonContainer, this.container);
    
    // Add clear and check buttons first
    const clearButton = document.createElement('button');
    clearButton.id = 'clear-' + Date.now(); // Unique ID
    clearButton.classList.add('btn', 'btn-outline-secondary', 'btn-sm', 'me-2', 'mb-2');
    clearButton.textContent = 'Alle löschen';
    clearButton.addEventListener('click', () => {
      this.clearSelections();
    });
    buttonContainer.appendChild(clearButton);
    
    const checkButton = document.createElement('button');
    checkButton.id = 'check-' + Date.now(); // Unique ID
    checkButton.classList.add('btn', 'btn-outline-secondary', 'btn-sm', 'me-2', 'mb-2');
    checkButton.textContent = 'Prüfen';
    checkButton.addEventListener('click', () => {
      const results = this.validate();
      alert(`Genauigkeit: ${Math.round(results.accuracy * 100)}%\nKorrekt: ${results.matches.length}/${this.groundTruth.length}`);
    });
    buttonContainer.appendChild(checkButton);
    
    // Create buttons for ALL possible categories (not just this sentence's)
    // Create buttons for categories found in this text
    this.categories.forEach(category => {
      const button = document.createElement('button');
      button.classList.add('btn', 'btn-sm', 'me-2', 'mb-2', 'text-nowrap');
      button.dataset.cat = category;
      
      // Use German label if available, otherwise use category key
      const label = this.categoryDict[category]?.de || category;
      const shortcut = this.getCategoryShortcut(category);
      button.textContent = `${label} (${shortcut})`;
      
      // Add keyboard shortcut hint in title
      button.title = `Tastenkürzel: ${shortcut}`;
      
        // Apply category color to button
        const categoryColor = this.getCategoryButtonColor(category);
        button.style.backgroundColor = categoryColor;
        button.style.borderColor = categoryColor;
        // Keep category contrast aligned with Bootstrap button text token
        button.style.color = 'var(--bs-btn-color)';
        // Darker shade on hover
        button.addEventListener('mouseenter', () => {
          button.style.backgroundColor = this.darkenColor(categoryColor);
        });
        button.addEventListener('mouseleave', () => {
          button.style.backgroundColor = categoryColor;
        });
        
      button.addEventListener('click', () => {
        // Use pre-selected word instead of browser selection
        if (this.preSelectedWord) {
          this.applyHighlightToPreSelected(category);
        } else {
          debug('No word pre-selected');
          alert('Please click on a word first to select it');
        }
      });
      
      buttonContainer.appendChild(button);
    });
  }

  tokenize(text) {
    return text.toLowerCase()
      // Replace non-word characters (except spaces) with spaces
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(token => token.length > 0);
  }

  addSelection(category) {
    // Prevent double execution
    if (this.isProcessingSelection) {
     debug('Already processing selection, returning early');
      return;
    }
    this.isProcessingSelection = true;
    
    if (!this.pendingSelection || !this.pendingSelection.text) {
     debug('No pending selection available');
      this.isProcessingSelection = false;
      return;
    }
    
    const { range } = this.pendingSelection;
   debug('Original selection:', this.pendingSelection.text);
   debug('Category:', category);
   
   // Find which sentence div contains this selection
   let sentenceDiv = null;
   let node = range.commonAncestorContainer;
   while (node && node !== this.container) {
     if (node.nodeType === Node.ELEMENT_NODE && node.classList && node.classList.contains('sentence')) {
       sentenceDiv = node;
       break;
     }
     node = node.parentNode;
   }
   
   debug('Sentence div found:', sentenceDiv);
   
   if (!sentenceDiv) {
     console.error('Selection is not within a sentence div!');
     alert('Please select text within a sentence');
     this.isProcessingSelection = false;
     return;
   }
   
   // Ensure the range is completely within the sentence div
   if (!sentenceDiv.contains(range.startContainer) || !sentenceDiv.contains(range.endContainer)) {
     console.error('Selection spans across sentence boundaries!');
     alert('Please select text within a single sentence');
     this.isProcessingSelection = false;
     return;
   }
    
    // Expand selection to word boundaries
    let expandedRange, expandedText;
    try {
      expandedRange = this.expandToWordBoundaries(range);
      expandedText = expandedRange.toString().trim();
     debug('Boundary expansion succeeded, text:', `"${expandedText}"`);
    } catch (error) {
      console.error('Boundary expansion failed:', error);
      debug('Falling back to original range');
      expandedRange = range.cloneRange();
      expandedText = range.toString().trim();
    }
    
    debug('Expanded text:', expandedText);
    debug('Range valid:', !expandedRange.collapsed, expandedRange.startContainer, expandedRange.endContainer);
    
    if (!expandedText) {
      alert('Could not determine word boundaries');
      this.isProcessingSelection = false;
      return;
    }
   
   debug('About to create highlight element...');
    
    // Create highlight element
    const highlight = document.createElement('span');
    // Use predictable CSS class names (special case for AdvT to avoid adblockers)
    const cssClass = category === 'AdvT' ? 'at' : category.toLowerCase();
    highlight.className = `gh-highlight ${cssClass}`;
    // DON'T SET data-cat - this was corrupting ground truth!
    // highlight.dataset.cat = category;  // <-- This was the bug!
      // Set data-category to the German name for tooltip display
      highlight.dataset.category = this.categoryDict[category]?.de || category;
    highlight.dataset.id = ++this.selectionId;
    
    // Add click listener to remove highlight
    highlight.addEventListener('click', (e) => {
      e.stopPropagation();
      // Check if highlights are locked
      if (!this.highlightsLocked) {
        this.removeSelection(highlight.dataset.id);
      }
    });
    
    try {
      debug('Attempting to surround contents...');
      
      // Instead of surroundContents, use a safer approach
      const text = expandedRange.toString().replace(/\s+/g, ' ').trim(); // Normalize whitespace like ground truth
     debug('Text to highlight:', `"${text}"`);
      
      // Check if this is a simple text selection within a single text node
      if (expandedRange.startContainer === expandedRange.endContainer && 
          expandedRange.startContainer.nodeType === Node.TEXT_NODE) {
        
        // Simple case: selection within a single text node
        const textNode = expandedRange.startContainer;
        const start = expandedRange.startOffset;
        const end = expandedRange.endOffset;
        
       debug('Text node content:', `"${textNode.textContent}"`);
       debug('Start offset:', start, 'End offset:', end);
       
        // Split the text node into three parts: before, selected, after
        const beforeText = textNode.textContent.substring(0, start);
        const selectedText = textNode.textContent.substring(start, end);
        const afterText = textNode.textContent.substring(end);
        
       debug('Before:', `"${beforeText}"`);
       debug('Selected:', `"${selectedText}"`);
       debug('After:', `"${afterText}"`);
       
        // Create new text nodes
        const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
        const afterNode = afterText ? document.createTextNode(afterText) : null;
        
        // Set the highlight content
        highlight.textContent = text; // Use same source as complex case
        
     // Ensure consistent whitespace handling across browsers
     highlight.style.whiteSpace = 'pre-wrap';
     
       debug('About to replace text node...');
       debug('Parent before:', textNode.parentNode.innerHTML);
       
        // Replace the original text node with the new structure
        const parent = textNode.parentNode;
        if (beforeNode) parent.insertBefore(beforeNode, textNode);
        parent.insertBefore(highlight, textNode);
        if (afterNode) parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);
        
       debug('Parent after:', parent.innerHTML);
       
      } else {
        // Complex case: just delete and insert
       debug('Complex case: delete and insert');
        highlight.textContent = text;
         highlight.style.whiteSpace = 'pre-wrap';
        expandedRange.deleteContents();
        expandedRange.insertNode(highlight);
      }
      
      debug('Successfully surrounded contents');
      
      // Store user selection (without corrupting ground truth)
      this.userSelections.push({
        id: this.selectionId,
        text: expandedText,
        category: category,
        tokens: this.tokenize(expandedText),
        element: highlight
      });
      
    // Apply rough notation immediately
    this.applyRoughNotationToElement(highlight, category);
    
     // Debug the highlight element styling
     debug('Highlight element:', highlight);
     debug('Highlight classes:', highlight.className);
    debug('Highlight textContent:', `"${highlight.textContent}"`);
    debug('Highlight innerHTML:', `"${highlight.innerHTML}"`);
    debug('Highlight childNodes:', highlight.childNodes);
     debug('Highlight computed styles:');
     const styles = window.getComputedStyle(highlight);
     debug('  color:', styles.color);
     debug('  background-color:', styles.backgroundColor);
     debug('  display:', styles.display);
     debug('  visibility:', styles.visibility);
     debug('  opacity:', styles.opacity);
     debug('  font-size:', styles.fontSize);
     
      debug('Stored user selection:', {
        id: this.selectionId,
        text: expandedText,
        category: category,
        element: highlight
      });
      
      // Clear the pending selection
      this.pendingSelection = null;
      window.getSelection().removeAllRanges();
      
      debug('About to try merging adjacent highlights...');
      // Try to merge with adjacent highlights of the same category (will skip SVGs)
      this.tryMergeAdjacentHighlights(this.selectionId);
      debug('Merging complete');
      
      // Update progress display
      this.updateProgressDisplay();
      
      // Clear processing flag
      this.isProcessingSelection = false;
      
    } catch (error) {
      console.error('Error adding selection:', error);
      console.error('Range details:', {
        collapsed: expandedRange.collapsed,
        startContainer: expandedRange.startContainer,
        endContainer: expandedRange.endContainer,
        startOffset: expandedRange.startOffset,
        endOffset: expandedRange.endOffset
      });
      this.isProcessingSelection = false;
      alert('Could not highlight the selected text. Try selecting simpler text boundaries.');
    }
  }

  tryMergeAdjacentHighlights(newSelectionId) {
    let hasMoreMerges = true;
    let currentSelectionId = newSelectionId;
    
    debug('=== Starting merge process for selection ID:', currentSelectionId);
    
    // Keep trying to merge until no more merges are possible
    while (hasMoreMerges) {
      hasMoreMerges = false;
      const currentSelection = this.userSelections.find(s => s.id == currentSelectionId);
      if (!currentSelection) {
        debug('Current selection not found, breaking');
        break;
      }
      
      debug('Current selection:', currentSelection.text, currentSelection.category);
      
      // Find other selections of the same category
      const sameCategory = this.userSelections.filter(s => 
        s.id != currentSelectionId && 
        s.category === currentSelection.category &&
        s.element && s.element.parentNode
      );
      
      debug('Same category selections:', sameCategory.map(s => s.text));
      
      for (const otherSelection of sameCategory) {
        debug('Checking adjacency with:', otherSelection.text);
        debug('Current element parent:', currentSelection.element.parentNode);
        debug('Other element parent:', otherSelection.element.parentNode);
        debug('Same parent?', currentSelection.element.parentNode === otherSelection.element.parentNode);
         
         // Additional debugging for boundary markers
         const currentSiblings = Array.from(currentSelection.element.parentNode.childNodes);
         const currentIndex = currentSiblings.indexOf(currentSelection.element);
         const otherIndex = currentSiblings.indexOf(otherSelection.element);
         debug('Current element index:', currentIndex);
         debug('Other element index:', otherIndex);
         debug('Nodes between:', currentSiblings.slice(Math.min(currentIndex, otherIndex) + 1, Math.max(currentIndex, otherIndex)));
         
        if (this.areElementsAdjacent(currentSelection.element, otherSelection.element)) {
          debug('Elements are adjacent! Merging...');
          // Merge and get the new selection ID
          const mergedSelectionId = this.mergeHighlights(currentSelection, otherSelection);
          if (mergedSelectionId) {
            debug('Merge successful, new ID:', mergedSelectionId);
            currentSelectionId = mergedSelectionId;
            hasMoreMerges = true;
            break; // Start over with the new merged element
          } else {
            debug('Merge failed');
          }
        } else {
          debug('Elements not adjacent');
        }
      }
    }
    debug('=== Merge process complete ===');
  }

  areElementsAdjacent(elem1, elem2) {
    debug('=== Checking adjacency ===');
    debug('elem1:', elem1.textContent, 'elem2:', elem2.textContent);
    
    // Check if both elements are in the same marked-text container
    const findMarkedTextParent = (element) => {
      let parent = element.parentNode;
      while (parent && parent !== this.container) {
        if (parent.nodeType === Node.ELEMENT_NODE && 
            parent.classList && 
            parent.classList.contains('marked-text')) {
          return parent;
        }
        parent = parent.parentNode;
      }
      return null;
    };
    
    const elem1MarkedParent = findMarkedTextParent(elem1);
    const elem2MarkedParent = findMarkedTextParent(elem2);
    
    debug('elem1 marked parent:', elem1MarkedParent?.textContent);
    debug('elem2 marked parent:', elem2MarkedParent?.textContent);
    
    // If they're not in the same marked-text container, they can't be adjacent
    if (elem1MarkedParent !== elem2MarkedParent || !elem1MarkedParent) {
      debug('Elements not in same marked-text container');
      return false;
    }
    
    const isIgnorableNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Just whitespace and punctuation (no more boundary markers to worry about)
        const result = /^[\s\p{P}]*$/u.test(node.textContent);
        return result;
      }
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'svg') {
        return true; // Always ignore SVG elements (rough notation)
      }
      // Ignore selection barrier elements
      if (node.nodeType === Node.ELEMENT_NODE && 
          node.classList && 
          node.classList.contains('selection-barrier')) {
        return true;
      }
      if (node.nodeType === Node.ELEMENT_NODE && node.classList && node.classList.contains('gh-highlight')) {
        const result = node.dataset.category === elem1.dataset.category;
        return result;
      }
     // Ignore marked-text spans - they can be merged through
     if (node.nodeType === Node.ELEMENT_NODE && node.classList && node.classList.contains('marked-text')) {
       return true; // Always ignore marked-text spans for adjacency check
     }
      return false;
    };
    
    // Since both elements are in the same marked-text container, check adjacency within that container
    // Use the direct parent of the elements for sibling checking
    const parent = elem1.parentNode;
    if (parent !== elem2.parentNode) {
      debug('Elements have different parents');
      return false;
    }
    
    const allNodes = Array.from(parent.childNodes);
    const elem1Index = allNodes.indexOf(elem1);
    const elem2Index = allNodes.indexOf(elem2);
    
    debug('elem1 index:', elem1Index, 'elem2 index:', elem2Index);
    
    if (elem1Index === -1 || elem2Index === -1) {
      debug('Could not find elements in parent');
      return false;
    }
    
    // Check if they're adjacent (with only ignorable nodes between)
    const startIndex = Math.min(elem1Index, elem2Index);
    const endIndex = Math.max(elem1Index, elem2Index);
    
    debug('Checking nodes between', startIndex, 'and', endIndex);
    
    for (let i = startIndex + 1; i < endIndex; i++) {
      const node = allNodes[i];
      debug('  node', i, ':', node.nodeType === Node.TEXT_NODE ? 
        `TEXT:"${node.textContent}" (length: ${node.textContent.length})` : 
        `${node.tagName}${node.className ? '.' + node.className : ''}`
      );
      
      if (!isIgnorableNode(node)) {
        debug('  ✗ Found non-ignorable node, not adjacent');
        debug('    Node details:', {
          nodeType: node.nodeType,
          tagName: node.tagName,
          className: node.className,
          textContent: node.textContent,
          isTextNode: node.nodeType === Node.TEXT_NODE,
          textRegexTest: node.nodeType === Node.TEXT_NODE ? /^[\s\p{P}]*$/u.test(node.textContent) : 'N/A'
        });
        // Additional check: if it's an unmarked-text span, it should have been caught above
        // If we get here with unmarked-text, it means hasUnmarkedTextBetween missed it
        if (node.nodeType === Node.ELEMENT_NODE && 
            node.classList && 
            node.classList.contains('unmarked-text')) {
          debug('  → Actually unmarked text, confirming non-adjacency');
          return false;
        }
        // For other non-ignorable nodes, still prevent merging
        return false;
      } else {
        debug('  → Ignoring node');
      }
    }
    
    debug('✓ Elements are adjacent');
    return true;
  }

  getMergedText(elem1, elem2) {
    // Determine order of elements in DOM
    const range = document.createRange();
    const comparison = elem1.compareDocumentPosition(elem2);
    
    if (comparison & Node.DOCUMENT_POSITION_FOLLOWING) {
      // elem1 comes before elem2
      range.setStartBefore(elem1);
      range.setEndAfter(elem2);
    } else {
      // elem2 comes before elem1
      range.setStartBefore(elem2);
      range.setEndAfter(elem1);
    }
    
    return range.toString().trim();
  }

  mergeHighlights(selection1, selection2) {
    debug('=== Starting merge process ===');
    debug('Merging:', selection1.text, 'with', selection2.text);
    
    // Determine DOM order of elements (work directly with spans)
    const comparison = selection1.element.compareDocumentPosition(selection2.element);
    
    let firstElement, secondElement, firstSelection, secondSelection;
    if (comparison & Node.DOCUMENT_POSITION_FOLLOWING) {
      firstElement = selection1.element;
      secondElement = selection2.element;
      firstSelection = selection1;
      secondSelection = selection2;
    } else {
      firstElement = selection2.element;
      secondElement = selection1.element;
      firstSelection = selection2;
      secondSelection = selection1;
    }
    
    debug('First element:', firstElement.textContent);
    debug('Second element:', secondElement.textContent);
    
    try {
      // Create a range to get the merged text content
    const range = document.createRange();
      range.setStartBefore(firstElement);
      range.setEndAfter(secondElement);
      const mergedText = range.toString().replace(/\s+/g, ' ').trim();
    const mergedTokens = this.tokenize(mergedText);
    
      debug('Merged text:', mergedText);
      
      // Check if there are any highlights between the elements that would prevent merging
      const nodesToPreserve = [];
      let current = firstElement.nextSibling;
      
      while (current && current !== secondElement) {
        debug('Checking intermediate node:', current);
        
        // Skip SVG elements but check their contents
        if (current.nodeType === Node.ELEMENT_NODE && current.tagName.toLowerCase() === 'svg') {
          debug('Skipping SVG element (rough notation)');
          current = current.nextSibling;
          continue;
        }
        
        // Skip whitespace
        if (current.nodeType === Node.TEXT_NODE && /^[\s\p{P}]*$/u.test(current.textContent)) {
          debug('Skipping whitespace');
          current = current.nextSibling;
          continue;
        }
        
        if (current.nodeType === Node.ELEMENT_NODE && 
            current.classList && 
            current.classList.contains('gh-highlight')) {
          
          const currentCategory = current.dataset.category;
          if (currentCategory !== firstSelection.category) {
            debug('Cannot merge - different category highlight in between');
            return null;
          }
          
          // Same category highlight - should be merged too
          nodesToPreserve.push(current);
        }
        
        current = current.nextSibling;
      }
      
      // Remove rough notation from elements being merged
      if (firstElement.roughAnnotation) {
        debug('Removing rough annotation from first element');
        firstElement.roughAnnotation.remove();
        firstElement.roughAnnotation = null;
      }
      
      if (secondElement.roughAnnotation) {
        debug('Removing rough annotation from second element');
        secondElement.roughAnnotation.remove();
        secondElement.roughAnnotation = null;
      }
      
      // Remove rough annotation from intermediate elements
      nodesToPreserve.forEach(node => {
        if (node.roughAnnotation) {
          debug('Removing rough annotation from intermediate element');
          node.roughAnnotation.remove();
          node.roughAnnotation = null;
        }
      });
      
      // Create new merged highlight
    const mergedHighlight = document.createElement('span');
      const cssClass = firstSelection.category === 'AdvT' ? 'at' : firstSelection.category.toLowerCase();
    mergedHighlight.className = `gh-highlight ${cssClass}`;
      // Set data-category to the German name for tooltip display
      mergedHighlight.dataset.category = this.categoryDict[firstSelection.category]?.de || firstSelection.category;
    mergedHighlight.dataset.id = ++this.selectionId;
    mergedHighlight.textContent = mergedText;
    
      // Add click listener
    mergedHighlight.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!this.highlightsLocked) {
        this.removeSelection(mergedHighlight.dataset.id);
      }
    });
    
      debug('Created merged highlight element');
      
      // Replace the range with the merged highlight
      const newRange = document.createRange();
      newRange.setStartBefore(firstElement);
      newRange.setEndAfter(secondElement);
      
      debug('Deleting range contents and inserting merged element');
      newRange.deleteContents();
      newRange.insertNode(mergedHighlight);
      
      // Apply rough notation to merged highlight
      this.applyRoughNotationToElement(mergedHighlight, firstSelection.category);
      
      // Remove old selections from array (including any intermediate selections)
      const selectionsToRemove = [firstSelection.id, secondSelection.id];
      nodesToPreserve.forEach(node => {
        const nodeId = node.dataset.id;
        if (nodeId) {
          selectionsToRemove.push(parseInt(nodeId));
        }
      });
      
      this.userSelections = this.userSelections.filter(s => 
        !selectionsToRemove.includes(s.id)
      );
      
      // Add merged selection
      this.userSelections.push({
        id: this.selectionId,
        text: mergedText,
        category: firstSelection.category,
        tokens: mergedTokens,
        element: mergedHighlight
      });
      
      debug('Merge completed successfully, new ID:', this.selectionId);
      return this.selectionId;
      
    } catch (error) {
      console.error('Error merging highlights:', error);
      return null;
    }
  }
  
  getTextBetweenElements(firstElement, secondElement) {
    let text = '';
    let current = firstElement;
    
    while (current && current !== secondElement.nextSibling) {
      if (current.nodeType === Node.TEXT_NODE) {
        text += current.textContent;
      } else if (current.nodeType === Node.ELEMENT_NODE) {
        text += current.textContent;
      }
      current = current.nextSibling;
    }
    
    return text.trim();
  }
  
  getNodesBetweenElements(firstElement, secondElement) {
    const nodes = [firstElement];
    let current = firstElement.nextSibling;
    
    while (current && current !== secondElement) {
      nodes.push(current);
      current = current.nextSibling;
    }
    
    if (current === secondElement) {
      nodes.push(secondElement);
    }
    
    return nodes;
  }

  lockHighlights() {
    this.highlightsLocked = true;
    this.userSelections.forEach(selection => {
      if (selection.element) {
        // Remove click handler and change appearance
        selection.element.style.cursor = 'default';
        selection.element.classList.add('opacity-75');
        selection.element.classList.add('locked');
      }
    });
  }

  unlockHighlights() {
    this.highlightsLocked = false;
    this.userSelections.forEach(selection => {
      if (selection.element) {
        // Restore click handler and appearance
        selection.element.style.cursor = 'pointer';
        selection.element.classList.remove('opacity-75');
        selection.element.classList.remove('locked');
      }
    });
  }

  validateSentence(sentenceGroundTruth, sentenceUserSelections) {
    const results = {
      score: 0,
      maxScore: sentenceGroundTruth.length,
      matches: [],
      accuracy: 0
    };
    
    if (sentenceGroundTruth.length === 0) return results;
    
    const matchedGround = new Set();
    
    sentenceUserSelections.forEach((userSel) => {
      let bestMatch = null;
      let bestScore = 0;
      
      sentenceGroundTruth.forEach((groundSel, groundIdx) => {
        if (matchedGround.has(groundIdx)) return;
        
        let score = 0;
        const categoryMatch = userSel.category === groundSel.category;
        
        if (this.matchMode === 'exact') {
          score = (userSel.text.toLowerCase() === groundSel.text.toLowerCase() && categoryMatch) ? 1 : 0;
        } else if (this.matchMode === 'token') {
         // First check for exact text match with correct category
         if (userSel.text.toLowerCase() === groundSel.text.toLowerCase() && categoryMatch) {
           score = 1; // Full credit for exact matches
         } else {
           // Otherwise use token-based scoring
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const tokenScore = union.length > 0 ? intersection.length / union.length : 0;
          
          if (categoryMatch) {
            // Give full credit if user selection is a subset of ground truth
            const isSubset = userSel.tokens.every(token => groundSel.tokens.includes(token));
            if (isSubset && userSel.tokens.length > 0) {
              score = userSel.tokens.length / groundSel.tokens.length;
            } else {
              score = tokenScore;
            }
          } else {
            score = 0;
          }
         }
        } else if (this.matchMode === 'iou') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const iou = union.length > 0 ? intersection.length / union.length : 0;
          score = (iou >= this.iouThreshold && categoryMatch) ? iou : 0;
        }
        
        if (score > bestScore) {
          bestMatch = { groundIdx, score };
          bestScore = score;
        }
      });
      
      if (bestMatch && bestScore > 0.5) {
        matchedGround.add(bestMatch.groundIdx);
        results.matches.push({
          user: userSel,
          ground: sentenceGroundTruth[bestMatch.groundIdx],
          score: bestMatch.score
        });
        results.score += bestMatch.score;
      }
    });
    
    results.accuracy = results.maxScore > 0 ? (results.score / results.maxScore) : 0;
    return results;
  }

  updateProgressDisplay() {
    if (!this.sentenceContainers || this.sentenceContainers.length === 0) return;
    
    // Debug: Log all ground truth items and their sentence indices
    debug('All ground truth items:');
    this.groundTruth.forEach((gt, idx) => {
      debug(`  ${idx}: "${gt.text}" (${gt.category}) -> sentence ${gt.sentenceIndex}`);
    });
    
    debug('Sentence containers:');
    this.sentenceContainers.forEach((sc, idx) => {
      debug(`  ${idx}: "${sc.text}" (index: ${sc.index})`);
    });
    
    // Calculate progress for each sentence
    this.sentenceContainers.forEach(sentenceContainer => {
      // Get ground truth items for this sentence
      const sentenceGroundTruth = this.groundTruth.filter(gt => 
        gt.sentenceIndex === sentenceContainer.index
      );
      
      debug(`Sentence ${sentenceContainer.index} ("${sentenceContainer.text}") has ${sentenceGroundTruth.length} ground truth items`);
      
     // Debug: Show which ground truth items are being included
     if (sentenceContainer.index === 0) {
       debug('First sentence ground truth items:');
       sentenceGroundTruth.forEach((gt, idx) => {
         debug(`  ${idx}: "${gt.text}" (${gt.category})`);
       });
     }
     
      // Get user selections for this sentence
      const sentenceUserSelections = this.userSelections.filter(sel => 
        sel.element && sentenceContainer.element.contains(sel.element)
      );
      
      // Calculate results for this sentence
      const results = this.validateSentence(sentenceGroundTruth, sentenceUserSelections);
     
     // Debug: Check why scores might be capped at 50%
     if (sentenceUserSelections.length > 0 || results.matches.length > 0) {
       debug(`Sentence ${sentenceContainer.index}:`, sentenceContainer.text.substring(0, 50) + '...');
       debug(`  Ground truth count: ${sentenceGroundTruth.length}`);
       debug(`  Ground truth items:`, sentenceGroundTruth.map(gt => `"${gt.text}" (${gt.category})`));
       debug(`  User selections: ${sentenceUserSelections.length}`);
       debug(`  User selection items:`, sentenceUserSelections.map(us => `"${us.text}" (${us.category})`));
       debug(`  Matches: ${results.matches.length}`);
       debug(`  Score: ${results.score} / ${results.maxScore} = ${results.accuracy}`);
       results.matches.forEach((match, idx) => {
         debug(`    Match ${idx}: "${match.user.text}" score=${match.score}`);
       });
       
       // Debug unmatched items
       const matchedGroundTruth = new Set(results.matches.map(m => m.ground.text + ':' + m.ground.category));
       const unmatchedGT = sentenceGroundTruth.filter(gt => 
         !matchedGroundTruth.has(gt.text + ':' + gt.category)
       );
       if (unmatchedGT.length > 0) {
         debug('  Unmatched ground truth:');
         unmatchedGT.forEach(gt => {
           debug(`    "${gt.text}" (${gt.category})`);
         });
       }
       
       // Check if we're getting all ground truth
       if (sentenceUserSelections.length === sentenceGroundTruth.length) {
         debug(`  ⚠️ All items selected but score is only ${Math.round(results.accuracy * 100)}%`);
       }
     }
     
      const percentage = Math.round(results.accuracy * 100);
      
      // Update percentage with color coding
      sentenceContainer.progressDisplay.textContent = `${percentage}%`;
      sentenceContainer.progressDisplay.style.color = this.getProgressColor(percentage);
      
      // Update details
      const correctCount = results.matches.length;
      const totalCount = sentenceGroundTruth.length;
      sentenceContainer.progressDetails.textContent = `(${correctCount}/${totalCount})`;
      
      // Add checkmark for perfect score
      if (percentage === 100 && totalCount > 0) {
        sentenceContainer.progressDetails.textContent = `(${correctCount}/${totalCount}) ✓`;
      }
    });
    
    // Lock/unlock highlights based on overall progress
    const allCorrect = this.sentenceContainers.every(sc => {
      const sentenceGroundTruth = this.groundTruth.filter(gt => 
        sc.text.includes(gt.text)
      );
      const sentenceUserSelections = this.userSelections.filter(sel => 
        sel.element && sc.element.contains(sel.element)
      );
      const results = this.validateSentence(sentenceGroundTruth, sentenceUserSelections);
      return results.accuracy === 1 && sentenceGroundTruth.length > 0;
    });
    
    if (allCorrect) {
      this.lockHighlights();
    } else {
      this.unlockHighlights();
    }
  }

  getProgressColor(percentage) {
    if (percentage >= 90) return 'var(--bs-success-text-emphasis)';
    if (percentage >= 70) return 'var(--bs-warning-text-emphasis)';
    if (percentage >= 50) return 'var(--bs-warning-text-emphasis)';
    return 'var(--bs-danger-text-emphasis)';
  }

  validateSilently() {
    // Same as validate() but without detailed output - used for real-time progress
    const results = {
      score: 0,
      maxScore: this.groundTruth.length,
      matches: [],
      accuracy: 0
    };
    
    if (this.groundTruth.length === 0) return results;
    
    const matchedGround = new Set();
    
    this.userSelections.forEach((userSel) => {
      let bestMatch = null;
      let bestScore = 0;
      
      this.groundTruth.forEach((groundSel, groundIdx) => {
        if (matchedGround.has(groundIdx)) return;
        
        let score = 0;
        const categoryMatch = userSel.category === groundSel.category;
        
        if (this.matchMode === 'exact') {
          score = (userSel.text.toLowerCase() === groundSel.text.toLowerCase() && categoryMatch) ? 1 : 0;
        } else if (this.matchMode === 'token') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const tokenScore = union.length > 0 ? intersection.length / union.length : 0;
          
          if (categoryMatch) {
            // Give full credit if user selection is a subset of ground truth
            const isSubset = userSel.tokens.every(token => groundSel.tokens.includes(token));
            if (isSubset && userSel.tokens.length > 0) {
              score = userSel.tokens.length / groundSel.tokens.length;
            } else {
              score = tokenScore;
            }
          } else {
            score = 0;
          }
        } else if (this.matchMode === 'iou') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const iou = union.length > 0 ? intersection.length / union.length : 0;
          score = (iou >= this.iouThreshold && categoryMatch) ? iou : 0;
        }
        
        if (score > bestScore) {
          bestMatch = { groundIdx, score };
          bestScore = score;
        }
      });
      
      if (bestMatch && bestScore > 0.5) {
        matchedGround.add(bestMatch.groundIdx);
        results.matches.push({
          user: userSel,
          ground: this.groundTruth[bestMatch.groundIdx],
          score: bestMatch.score
        });
        results.score += bestMatch.score;
      }
    });
    
    results.accuracy = results.maxScore > 0 ? (results.score / results.maxScore) : 0;
    return results;
  }

  expandToWordBoundaries(range) {
    // Simple approach: just expand the current selection to word boundaries
    // without trying to cross container boundaries
    const text = range.toString();
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;
    
   debug('expandToWordBoundaries:');
   debug('  Start container:', startContainer);
   debug('  End container:', endContainer);
   debug('  Start container text:', startContainer.nodeType === Node.TEXT_NODE ? `"${startContainer.textContent}"` : 'not text');
   debug('  End container text:', endContainer.nodeType === Node.TEXT_NODE ? `"${endContainer.textContent}"` : 'not text');
   
    // If the selection spans multiple nodes, don't try to expand
    if (startContainer !== endContainer) {
     debug('  Selection spans multiple nodes - not expanding');
      return range.cloneRange();
    }
    
    // For text nodes, expand within the same node
    if (startContainer.nodeType === Node.TEXT_NODE) {
      const nodeText = startContainer.textContent;
      const wordBoundary = /[\s\p{P}]/u;
      
      let start = range.startOffset;
      let end = range.endOffset;
      
     debug(`  Expanding within text node: "${nodeText}"`);
     debug(`  Original offsets: ${start}-${end} ("${nodeText.substring(start, end)}")`);
     
      // Expand backward
      while (start > 0 && !wordBoundary.test(nodeText[start - 1])) {
        start--;
      }
      
      // Expand forward
      while (end < nodeText.length && !wordBoundary.test(nodeText[end])) {
        end++;
      }
      
     debug(`  Expanded offsets: ${start}-${end} ("${nodeText.substring(start, end)}")`);
     
      const newRange = document.createRange();
      newRange.setStart(startContainer, start);
      newRange.setEnd(startContainer, end);
      return newRange;
    }
    
    // For other cases, just return the original range
    return range.cloneRange();
  }

  getAbsoluteOffset(container, node, offset) {
    let absoluteOffset = 0;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let currentNode;
    while (currentNode = walker.nextNode()) {
      if (currentNode === node) {
        return absoluteOffset + offset;
      }
      absoluteOffset += currentNode.textContent.length;
    }
    
    return absoluteOffset;
  }

  getNodeAndOffsetAt(container, absoluteOffset) {
    let currentOffset = 0;
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );
    
    let currentNode;
    while (currentNode = walker.nextNode()) {
      const nodeLength = currentNode.textContent.length;
      if (currentOffset + nodeLength >= absoluteOffset) {
        return {
          node: currentNode,
          offset: absoluteOffset - currentOffset
        };
      }
      currentOffset += nodeLength;
    }
    
    // If we get here, return the end of the last text node
    return {
      node: currentNode || container,
      offset: currentNode ? currentNode.textContent.length : 0
    };
  }

  removeSelection(id) {
    const selection = this.userSelections.find(s => s.id == id);
    if (!selection) return;
    
    const element = selection.element;
    const parent = element.parentNode;
    
    // Remove rough notation annotation first
    if (element.roughAnnotation) {
      try {
      element.roughAnnotation.remove();
        element.roughAnnotation = null;
      } catch (error) {
        console.error('Error removing rough annotation:', error);
      }
    }
    
    // Replace highlight with plain text
    const textNode = document.createTextNode(element.textContent);
    parent.replaceChild(textNode, element);
    // Don't normalize - can cause spacing issues in Edge
    // parent.normalize();
    
    // Remove from selections array
    this.userSelections = this.userSelections.filter(s => s.id != id);

    // Update progress display
    this.updateProgressDisplay();
  }

  clearSelections() {
    // Remove all highlights and restore plain text
    this.userSelections.forEach(selection => {
      if (selection.element) {
        // Remove rough notation annotation first
        if (selection.element.roughAnnotation) {
          try {
          selection.element.roughAnnotation.remove();
          } catch (error) {
            console.error('Error removing rough annotation:', error);
          }
        }
        
        const parent = selection.element.parentNode;
        if (parent) {
          const textNode = document.createTextNode(selection.element.textContent);
          parent.replaceChild(textNode, selection.element);
          // Don't normalize - can cause spacing issues in Edge
          // parent.normalize();
        }
      }
    });
    
    this.userSelections = [];
    this.pendingSelection = null;
    window.getSelection().removeAllRanges();

    // Update progress display
    this.updateProgressDisplay();
  }

  validate() {
    const results = {
      score: 0,
      maxScore: this.groundTruth.length,
      matches: [],
      missed: [],
      incorrect: [],
      details: {}
    };
    
    // Track which ground truth items have been matched
    const matchedGround = new Set();
    const matchedUser = new Set();
    
    // Check each user selection against ground truth
    this.userSelections.forEach((userSel, userIdx) => {
      let bestMatch = null;
      let bestScore = 0;
      let bestTextMatch = null; // Track best text match regardless of category
      
      this.groundTruth.forEach((groundSel, groundIdx) => {
        if (matchedGround.has(groundIdx)) return;
        
        let score = 0;
        const categoryMatch = userSel.category === groundSel.category;
        
        // Calculate text similarity regardless of category for error classification
        let textScore = 0;
        if (this.matchMode === 'exact') {
          textScore = (userSel.text.toLowerCase() === groundSel.text.toLowerCase()) ? 1 : 0;
        } else if (this.matchMode === 'token') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          textScore = union.length > 0 ? intersection.length / union.length : 0;
        } else if (this.matchMode === 'iou') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          textScore = union.length > 0 ? intersection.length / union.length : 0;
        }
        
        // Track best text match for error classification
        if (!bestTextMatch || textScore > bestTextMatch.textScore) {
          bestTextMatch = { groundIdx, textScore, categoryMatch };
        }
        
        if (this.matchMode === 'exact') {
          score = (userSel.text.toLowerCase() === groundSel.text.toLowerCase() && categoryMatch) ? 1 : 0;
        } else if (this.matchMode === 'token') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const tokenScore = union.length > 0 ? intersection.length / union.length : 0;
          
          if (categoryMatch) {
            // Give partial credit if user selection is a subset of ground truth
            const isSubset = userSel.tokens.every(token => groundSel.tokens.includes(token));
            if (isSubset && userSel.tokens.length > 0) {
              score = userSel.tokens.length / groundSel.tokens.length;
            } else {
              score = tokenScore;
            }
          } else {
            score = 0;
          }
        } else if (this.matchMode === 'iou') {
          const intersection = userSel.tokens.filter(token => groundSel.tokens.includes(token));
          const union = [...new Set([...userSel.tokens, ...groundSel.tokens])];
          const iou = union.length > 0 ? intersection.length / union.length : 0;
          score = (iou >= this.iouThreshold && categoryMatch) ? iou : 0;
        }
        
        if (score > bestScore) {
          bestMatch = { groundIdx, score, categoryMatch };
          bestScore = score;
        }
      });
      
      if (bestMatch && bestScore > 0.5) {
        matchedGround.add(bestMatch.groundIdx);
        matchedUser.add(userIdx);
        results.matches.push({
          user: userSel,
          ground: this.groundTruth[bestMatch.groundIdx],
          score: bestMatch.score
        });
        results.score += bestMatch.score;
      } else {
        // Use bestTextMatch to determine error type more accurately
        let reason = 'wrong_text';
        if (bestTextMatch && bestTextMatch.textScore > 0.5 && !bestTextMatch.categoryMatch) {
          reason = 'wrong_category';
        }
        results.incorrect.push({
          user: userSel,
          reason: reason
        });
      }
    });
    
    // Find missed ground truth items
    this.groundTruth.forEach((groundSel, idx) => {
      if (!matchedGround.has(idx)) {
        results.missed.push(groundSel);
      }
    });
    
    results.accuracy = results.maxScore > 0 ? (results.score / results.maxScore) : 0;
    results.details = {
      matchMode: this.matchMode,
      iouThreshold: this.iouThreshold,
      totalSelections: this.userSelections.length,
      correctSelections: results.matches.length
    };
    
    return results;
  }

  // Utility method to get current state
  getState() {
    return {
      text: this.currentText,
      groundTruth: this.groundTruth,
      userSelections: this.userSelections.map(sel => ({
        text: sel.text,
        category: sel.category,
        tokens: sel.tokens
      }))
    };
  }

  getCategoryShortcut(category) {
    // Return global shortcut or fallback
    return this.globalShortcuts[category] || category.toLowerCase();
  }

  handleKeyboardShortcut(e) {
    // Every instance listens on document, so only the one with a word picked
    // acts; without this, single letters were swallowed page-wide (reveal.js
    // shortcuts, type-ahead) as soon as a .syntax block was on the page.
    if (!this.preSelectedWord) return;
    // Ignore if user is typing in an input field
    const t = e.target;
    if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable) return;
    
    // Ignore modifier keys
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    
    // Convert key to lowercase to handle both uppercase and lowercase input
    const key = e.key.toLowerCase();
    
    // Only handle alphabetic keys
    if (!/^[a-z]$/.test(key)) return;
    
    e.preventDefault();
    
    // Clear existing timeout
    if (this.keyTimeout) {
      clearTimeout(this.keyTimeout);
    }
    
    // Add to buffer
    this.keyBuffer += key;
    
    // Try to match against categories
    const matchedCategory = this.findCategoryByShortcut(this.keyBuffer);
    
    if (matchedCategory) {
      // Use pre-selected word with keyboard shortcut
      if (this.preSelectedWord) {
        this.applyHighlightToPreSelected(matchedCategory);
      } else {
        debug('No word pre-selected for keyboard shortcut');
      }
      this.keyBuffer = '';
      return;
    }
    
    // Set timeout to clear buffer
    this.keyTimeout = setTimeout(() => {
      this.keyBuffer = '';
    }, this.keyTimeoutDuration);
  }

  findCategoryByShortcut(buffer) {
    // Check against ALL possible categories (not just this sentence's)
   debug('Finding category for buffer:', buffer);
    for (const category of this.allCategories) {
      if (this.globalShortcuts[category] === buffer) {
       debug('Found match:', category, 'for shortcut:', buffer);
        return category;
      }
    }
    
   debug('No match found for buffer:', buffer);
    return null;
  }
}
